interface Quadrilateral {
  topLeftX: number;
  topLeftY: number;
  topRightX: number;
  topRightY: number;
  bottomRightX: number;
  bottomRightY: number;
  bottomLeftX: number;
  bottomLeftY: number;
}

let glContext: {
  canvas: HTMLCanvasElement;
  gl: WebGLRenderingContext;
  program: WebGLProgram;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  locations: Record<string, any>;
  buffer: WebGLBuffer;
  texture: WebGLTexture;
} | null = null;

let cachedImage: HTMLImageElement | HTMLCanvasElement | null = null;

function getGLContext() {
  if (glContext) return glContext;

  const canvas = document.createElement('canvas');
  const gl = canvas.getContext('webgl2', {
    premultipliedAlpha: true,
    antialias: true,
  });
  if (!gl) throw new Error('WebGL not supported');

  const vsSource = `
    attribute vec2 a_position;
    varying vec2 v_localPos;
    uniform vec2 u_size;
    uniform vec2 u_center;

    void main() {
      // Expand the corners outward by 0.75 pixels to create a seamless overlap
      vec2 dir = normalize(a_position - u_center);
      vec2 expanded_pos = a_position + (dir * (0.75 / u_size));

      v_localPos = expanded_pos * u_size;
      vec2 clipSpace = expanded_pos * 2.0 - 1.0;
      gl_Position = vec4(clipSpace * vec2(1.0, -1.0), 0.0, 1.0);
    }
  `;

  const fsSource = `
    precision highp float;
    uniform sampler2D u_image;
    uniform float u_matrix[8];
    uniform vec4 u_rect; // u0, u1, v0, v1 (the repeat ranges)
    uniform vec2 u_corners[4]; // TL, TR, BR, BL in local pixels
    varying vec2 v_localPos;

    // Helper for inverse bilinear interpolation
    float cross2d(vec2 a, vec2 b) {
        return a.x * b.y - a.y * b.x;
    }

    void main() {
      vec2 p = v_localPos;

      // 1. HORIZONTAL: Calculate true perspective U using the matrix
      float denominator = u_matrix[6] * p.x + u_matrix[7] * p.y + 1.0;
      float srcX = (u_matrix[0] * p.x + u_matrix[1] * p.y + u_matrix[2]) / denominator;

      // 2. VERTICAL: Calculate perfectly linear V using Inverse Bilinear Interpolation
      vec2 a = u_corners[0]; // Top Left
      vec2 b = u_corners[1]; // Top Right
      vec2 c = u_corners[2]; // Bottom Right
      vec2 d = u_corners[3]; // Bottom Left

      vec2 e = b - a;
      vec2 f = d - a;
      vec2 g = a - b + c - d;
      vec2 h = p - a;

      float k2 = cross2d(g, f);
      float k1 = cross2d(e, f) + cross2d(h, g);
      float k0 = cross2d(h, e);

      float v_norm = 0.0;

      // If edges are perfectly parallel, it's a simple linear equation
      if (abs(k2) < 0.00001) {
          v_norm = -k0 / k1;
      } else {
          // Otherwise, solve the quadratic equation
          float w = k1 * k1 - 4.0 * k0 * k2;
          if (w < 0.0) discard; // Prevent artifacts outside bounds

          w = sqrt(w);

          // make sure there is always a sgn and never 0.0
          float sgn = k1 >= 0.0 ? 1.0 : -1.0;
          float q = -0.5 * (k1 + sgn * w);

          float v1 = q / k2;
          float v2 = k0 / q;

          // Pick the valid root that falls between 0.0 and 1.0
          // (Usually, v2 is a good fallback for near-rectangles)
          v_norm = (v1 >= 0.0 && v1 <= 1.0) ? v1 : v2;
      }

      // Map the normalized 0..1 coordinate back to your repeats (v0 to v1)
      float v0 = u_rect.z;
      float v1 = u_rect.w;
      float srcY = mix(v0, v1, v_norm);

      // 3. Combine perspective U and linear V!
      vec2 uv = vec2(srcX, srcY);
      gl_FragColor = texture2D(u_image, uv);
    }
  `;

  function compileShader(type: number, source: string) {
    const shader = gl!.createShader(type)!;
    gl!.shaderSource(shader, source);
    gl!.compileShader(shader);
    return shader;
  }

  const program = gl.createProgram()!;
  gl.attachShader(program, compileShader(gl.VERTEX_SHADER, vsSource));
  gl.attachShader(program, compileShader(gl.FRAGMENT_SHADER, fsSource));
  gl.linkProgram(program);

  const buffer = gl.createBuffer()!;
  gl.bindBuffer(gl.ARRAY_BUFFER, buffer);

  const texture = gl.createTexture()!;
  gl.bindTexture(gl.TEXTURE_2D, texture);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.REPEAT);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.REPEAT);
  gl.texParameteri(
    gl.TEXTURE_2D,
    gl.TEXTURE_MIN_FILTER,
    gl.LINEAR_MIPMAP_LINEAR,
  );
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);

  glContext = {
    canvas,
    gl,
    program,
    buffer,
    texture,
    locations: {
      position: gl.getAttribLocation(program, 'a_position'),
      size: gl.getUniformLocation(program, 'u_size'),
      matrix: gl.getUniformLocation(program, 'u_matrix'),
      rect: gl.getUniformLocation(program, 'u_rect'),
      corners: gl.getUniformLocation(program, 'u_corners'),
      center: gl.getUniformLocation(program, 'u_center'),
    },
  };

  return glContext;
}

export default class Perspective {
  private ctx: CanvasRenderingContext2D;
  private image: HTMLImageElement | HTMLCanvasElement;

  constructor(
    ctx: CanvasRenderingContext2D,
    image: HTMLImageElement | HTMLCanvasElement,
  ) {
    this.ctx = ctx;
    this.image = image;
  }

  draw(
    q: Quadrilateral,
    src: { u0: number; u1: number; v0: number; v1: number },
  ) {
    const {
      topLeftX,
      topLeftY,
      topRightX,
      topRightY,
      bottomRightX,
      bottomRightY,
      bottomLeftX,
      bottomLeftY,
    } = q;

    const minX =
      Math.floor(Math.min(topLeftX, topRightX, bottomRightX, bottomLeftX)) - 1;
    const maxX =
      Math.ceil(Math.max(topLeftX, topRightX, bottomRightX, bottomLeftX)) + 1;
    const minY =
      Math.floor(Math.min(topLeftY, topRightY, bottomRightY, bottomLeftY)) - 1;
    const maxY =
      Math.ceil(Math.max(topLeftY, topRightY, bottomRightY, bottomLeftY)) + 1;

    const destWidth = maxX - minX;
    const destHeight = maxY - minY;

    if (destWidth <= 0 || destHeight <= 0) return;

    const transform = getPerspectiveTransform(
      topLeftX - minX,
      topLeftY - minY,
      topRightX - minX,
      topRightY - minY,
      bottomRightX - minX,
      bottomRightY - minY,
      bottomLeftX - minX,
      bottomLeftY - minY,
      src.u0,
      src.v0,
      src.u1,
      src.v0,
      src.u1,
      src.v1,
      src.u0,
      src.v1,
    );

    if (!transform) return;

    const ctx = getGLContext();
    const { gl, canvas, program, locations, buffer, texture } = ctx;

    canvas.width = destWidth;
    canvas.height = destHeight;
    gl.viewport(0, 0, destWidth, destHeight);

    gl.useProgram(program);

    if (cachedImage !== this.image) {
      gl.bindTexture(gl.TEXTURE_2D, texture);
      gl.texImage2D(
        gl.TEXTURE_2D,
        0,
        gl.RGBA,
        gl.RGBA,
        gl.UNSIGNED_BYTE,
        this.image,
      );
      gl.generateMipmap(gl.TEXTURE_2D);
      cachedImage = this.image;
    }

    gl.uniform2f(locations.size, destWidth, destHeight);
    gl.uniform1fv(locations.matrix, new Float32Array(transform));

    gl.uniform4f(locations.rect, src.u0, src.u1, src.v0, src.v1);

    gl.uniform2fv(
      locations.corners,
      new Float32Array([
        topLeftX - minX,
        topLeftY - minY,
        topRightX - minX,
        topRightY - minY,
        bottomRightX - minX,
        bottomRightY - minY,
        bottomLeftX - minX,
        bottomLeftY - minY,
      ]),
    );

    // relative to its bounding box (values between 0.0 and 1.0)
    const tlX = (topLeftX - minX) / destWidth;
    const tlY = (topLeftY - minY) / destHeight;
    const trX = (topRightX - minX) / destWidth;
    const trY = (topRightY - minY) / destHeight;
    const blX = (bottomLeftX - minX) / destWidth;
    const blY = (bottomLeftY - minY) / destHeight;
    const brX = (bottomRightX - minX) / destWidth;
    const brY = (bottomRightY - minY) / destHeight;

    const centerX = (tlX + trX + blX + brX) / 4.0;
    const centerY = (tlY + trY + blY + brY) / 4.0;
    gl.uniform2f(locations.center, centerX, centerY);

    gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
    gl.bufferData(
      gl.ARRAY_BUFFER,
      new Float32Array([tlX, tlY, trX, trY, blX, blY, brX, brY]),
      gl.DYNAMIC_DRAW,
    );

    gl.enableVertexAttribArray(locations.position);
    gl.vertexAttribPointer(locations.position, 2, gl.FLOAT, false, 0, 0);
    gl.clearColor(0, 0, 0, 0);
    gl.clear(gl.COLOR_BUFFER_BIT);
    gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);

    this.ctx.drawImage(canvas, minX, minY);
  }
}

function gaussianElimination(A: number[][], b: number[]): number[] | null {
  const n = A.length;
  const augmented = A.map((row, i) => [...row, b[i]]);

  for (let i = 0; i < n; i++) {
    let maxRow = i;
    for (let k = i + 1; k < n; k++) {
      if (Math.abs(augmented[k][i]) > Math.abs(augmented[maxRow][i])) {
        maxRow = k;
      }
    }

    [augmented[i], augmented[maxRow]] = [augmented[maxRow], augmented[i]];

    if (Math.abs(augmented[i][i]) < 1e-10) return null;

    for (let k = i + 1; k < n; k++) {
      const factor = augmented[k][i] / augmented[i][i];
      for (let j = i; j <= n; j++) {
        augmented[k][j] -= factor * augmented[i][j];
      }
    }
  }

  const x = new Array(n);
  for (let i = n - 1; i >= 0; i--) {
    x[i] = augmented[i][n];
    for (let j = i + 1; j < n; j++) {
      x[i] -= augmented[i][j] * x[j];
    }
    x[i] /= augmented[i][i];
  }

  return x;
}

/**
 * Computes the 8 coefficients of a 2D perspective transform that maps the
 * destination quad (x0..x3,y0..y3) to source UVs (u0..u3,v0..v3). Returns null
 * if the system is degenerate.
 */
export function getPerspectiveTransform(
  x0: number,
  y0: number,
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  x3: number,
  y3: number,
  u0: number,
  v0: number,
  u1: number,
  v1: number,
  u2: number,
  v2: number,
  u3: number,
  v3: number,
): number[] | null {
  const A = [
    [x0, y0, 1, 0, 0, 0, -u0 * x0, -u0 * y0],
    [x1, y1, 1, 0, 0, 0, -u1 * x1, -u1 * y1],
    [x2, y2, 1, 0, 0, 0, -u2 * x2, -u2 * y2],
    [x3, y3, 1, 0, 0, 0, -u3 * x3, -u3 * y3],
    [0, 0, 0, x0, y0, 1, -v0 * x0, -v0 * y0],
    [0, 0, 0, x1, y1, 1, -v1 * x1, -v1 * y1],
    [0, 0, 0, x2, y2, 1, -v2 * x2, -v2 * y2],
    [0, 0, 0, x3, y3, 1, -v3 * x3, -v3 * y3],
  ];

  const b = [u0, u1, u2, u3, v0, v1, v2, v3];
  return gaussianElimination(A, b);
}
